import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  FwButtonComponent,
  FwDialogActionsComponent,
  FwDialogComponent,
  FwDialogHeaderComponent,
  FwLayoutPanelComponent,
  FwTypeaheadComponent,
  FwWrappedInputComponent,
} from '@flywheel-io/vision';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { union } from 'lodash';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsQueryService } from '../../projects/projects-query.service';
import { ContainerType } from '../../shared/enums/container-type.enum';
import { SearchResult } from '../../shared/models/search/search-results.model';
import { SidebarService } from '../../sidebar.service';

interface DialogData {
  checkedContainers: Set<string>;
  resultType: string;
}
@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'modify-tags-dialog',
  },
  selector: 'modify-tags-dialog',
  imports: [
    ReactiveFormsModule,
    FwLayoutPanelComponent,
    FwDialogComponent,
    FwDialogHeaderComponent,
    FwDialogActionsComponent,
    FwWrappedInputComponent,
    FwTypeaheadComponent,
    FwButtonComponent,
  ],
  styleUrls: ['./modify-tags-dialog.component.scss'],
  templateUrl: './modify-tags-dialog.component.html',
})
export class ModifyTagsDialog {
  private coreApi = inject(CoreApiService);
  private notifications = inject(NotificationService);
  private logger = inject(LoggingService);
  private projectsQueryService = inject(ProjectsQueryService);
  private sidebar = inject(SidebarService);
  protected dialogRef = inject(DialogRef);
  private data = inject<DialogData>(DIALOG_DATA, { optional: true });

  form = new FormGroup({
    removeTagInput: new FormControl<string[]>([]),
    addTagInput: new FormControl<string[]>([]),
  });

  selectedContainers = Array.from(this.data.checkedContainers).map((containerId) => ({
    id: containerId,
    type: this.data.resultType,
  }));

  groupsQuery = injectQuery(() => this.projectsQueryService.allGroupsEffective());

  private allTags = computed(() => {
    const groups = this.groupsQuery.data() ?? [];
    return groups.reduce((acc, group) => union(acc, group.tags), [] as string[]);
  });

  private addSelected = toSignal(this.form.controls.addTagInput.valueChanges, {
    initialValue: [] as string[],
  });
  private removeSelected = toSignal(this.form.controls.removeTagInput.valueChanges, { initialValue: [] as string[] });

  filteredTagOptions = computed(() => {
    const excluded = new Set([...(this.addSelected() ?? []), ...(this.removeSelected() ?? [])]);
    return this.allTags().filter((tag) => !excluded.has(tag));
  });

  hasSelections = computed(() => (this.addSelected()?.length ?? 0) > 0 || (this.removeSelected()?.length ?? 0) > 0);

  submit(): void {
    // Acquisition and Analyses have different IDs that need to be used
    let containers = this.selectedContainers;
    let searchResults: SearchResult[];
    const containerIds = Array.from(containers).map((container) => container.id);

    if (this.data.resultType === ContainerType.Acquisition) {
      searchResults = this.sidebar.search.results$.value.filter((row) => containerIds.includes(row._id));
      containers = searchResults.map((item) => {
        return { id: item._source.acquisition._id, type: this.data.resultType };
      });
    }

    if (this.data.resultType === ContainerType.Analysis) {
      searchResults = this.sidebar.search.results$.value.filter((row) => containerIds.includes(row._id));
      containers = searchResults.map((item) => {
        return { id: item._source.analysis._id, type: this.data.resultType };
      });
    }

    const tagsToAdd: string[] = this.form.value.addTagInput ?? [];
    const tagsToRemove: string[] = this.form.value.removeTagInput ?? [];

    if (tagsToAdd.length > 0) {
      this.addBulkTags(containers, tagsToAdd);
    }

    if (tagsToRemove.length > 0) {
      this.removeBulkTags(containers, tagsToRemove);
    }
  }

  private addBulkTags(containers, tags: string[]): void {
    this.coreApi.bulk.tags
      .post({
        tags,
        containers,
      })
      .subscribe({
        next: (resp) => {
          // On a 200, resp can contain both successful and failed tag submissions
          if (resp.success.length > 0) {
            this.notifications.success('modifyTagsDialog.addBulkTags.success');
          }
          if (resp.error.length > 0) {
            this.notifications.error('modifyTagsDialog.addBulkTags.error');
          }
          this.dialogRef.close(resp);
        },
        error: (error) => {
          this.logger.error(error);
          this.notifications.error('modifyTagsDialog.addBulkTags.error');
        },
      });
  }

  private removeBulkTags(containers, tags: string[]): void {
    this.coreApi.bulk.tags
      .delete({
        tags,
        containers,
      })
      .subscribe({
        next: (resp) => {
          // On a 200, resp can contain both successful and failed tag submissions
          if (resp.success.length > 0) {
            this.notifications.success('modifyTagsDialog.removeBulkTags.success');
          }
          if (resp.error.length > 0) {
            this.notifications.error('modifyTagsDialog.removeBulkTags.error');
          }
          this.dialogRef.close(resp);
        },
        error: (error) => {
          this.logger.error(error);
          this.notifications.error('modifyTagsDialog.removeBulkTags.error');
        },
      });
  }
}
